import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { formatDateDisplay } from '../../utils/date-utils';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ButtonComponent } from '../button/button.component';
import { CardComponent } from '../card/card.component';
import { CollapsibleSectionComponent } from '../collapsible-section/collapsible-section.component';
import { ProfileSectionRowComponent } from '../profile-section-row/profile-section-row.component';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { LanguageExtended, SkillExtended, HighSchoolEducationExtended, UniversityEducationExtended, WorkExperienceExtended, ProjectExtended } from '../../../../../../libs/shared/src/lib/models/dashboard/user-from-be';
import { LanguagesFormComponent } from './languages-form/languages-form.component';
import { SkillsFormComponent } from './skills-form/skills-form.component';
import { HighSchoolFormComponent } from './high-school-form/high-school-form.component';
import { UniversityFormComponent } from './university-form/university-form.component';
import { WorkExperienceFormComponent } from './work-experience-form/work-experience-form.component';
import { ProjectFormComponent } from './project-form/project-form.component';
import { CvOptions } from '../../models/cv-options.model';
// All validation constants are now accessed through ValidationService for consistency
import { ValidationService } from '../../../../../../libs/shared/src/lib/services/validation.service';
import { EnumLabelPipe } from '../../utils/enum-label.pipe';

@Component({
  selector: 'app-profile-section',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonComponent,
    CardComponent,
    CollapsibleSectionComponent,
    ProfileSectionRowComponent,
    TranslocoPipe,
    LanguagesFormComponent,
    SkillsFormComponent,
    HighSchoolFormComponent,
    UniversityFormComponent,
    WorkExperienceFormComponent,
    ProjectFormComponent,
    EnumLabelPipe,
  ],
  templateUrl: './profile-section.component.html',
  styleUrls: ['./profile-section.component.scss'],
})
export class ProfileSectionComponent implements OnInit, OnChanges {
  @Input() title: string = '';
  @Input() sectionType: 'languages' | 'skills' | 'highSchool' | 'university' | 'workExperience' | 'projects' = 'languages';
  @Input() languages: LanguageExtended[] = [];
  @Input() skills: SkillExtended[] = [];
  @Input() highSchools: HighSchoolEducationExtended[] = [];
  @Input() universities: UniversityEducationExtended[] = [];
  @Input() workExperiences: WorkExperienceExtended[] = [];
  @Input() projects: ProjectExtended[] = [];
  @Input() cvOptions: CvOptions | null = null;
  @Input() isAuthenticated = false;

  // Unified event emitter for languages, skills, high school, university education, work experience and projects
  @Output() profileItemAction = new EventEmitter<{
    itemType: 'languages' | 'skills' | 'highSchool' | 'university' | 'workExperience' | 'projects';
    action: 'add' | 'edit' | 'delete' | 'save' | 'batchSave';
    item?: LanguageExtended | SkillExtended | HighSchoolEducationExtended | UniversityEducationExtended | WorkExperienceExtended | ProjectExtended;
    items?: any[]; // For batch operations
    index?: number;
    isAutoSave?: boolean;
  }>();

  // Form state
  showLanguageForm = false;
  showSkillForm = false;
  showHighSchoolForm = false;
  showUniversityForm = false;
  showWorkExperienceForm = false;
  showProjectForm = false;

  // Editing state
  editingLanguage: LanguageExtended | null = null;
  editingSkill: SkillExtended | null = null;
  editingHighSchool: HighSchoolEducationExtended | null = null;
  editingUniversity: UniversityEducationExtended | null = null;
  editingWorkExperience: WorkExperienceExtended | null = null;
  editingProject: ProjectExtended | null = null;
  editingIndex: number | null = null;

  // Auto-save control flag
  private allowAutoSave = false;
  // Flag to prevent autosave during operations like delete, edit, etc.
  private skipNextAutoSave = false;

  /**
   * Returns the count of items in the active section. Used by the template
   * to decide whether the section's <details> wrapper opens by default
   * (short lists stay expanded; long lists collapse on mount so the
   * sidebar/job-apply form does not get pushed off-screen).
   */
  get itemCount(): number {
    switch (this.sectionType) {
      case 'languages': return this.languages?.length ?? 0;
      case 'skills': return this.skills?.length ?? 0;
      case 'highSchool': return this.highSchools?.length ?? 0;
      case 'university': return this.universities?.length ?? 0;
      case 'workExperience': return this.workExperiences?.length ?? 0;
      case 'projects': return this.projects?.length ?? 0;
      default: return 0;
    }
  }

  /**
   * True when any inline form is currently being edited or shown for
   * "add new". Forces the collapsible to stay open while the user is
   * mid-flow.
   */
  get isEditingOrAdding(): boolean {
    return this.editingIndex !== null
      || this.showLanguageForm
      || this.showSkillForm
      || this.showHighSchoolForm
      || this.showUniversityForm
      || this.showWorkExperienceForm
      || this.showProjectForm;
  }


  constructor(
    private translocoService: TranslocoService,
    private validationService: ValidationService
  ) {}

  /**
   * Translate skill level for display. Accepts canonical enum values
   * (`beginner`, `BEGINNER`, `db.beginner`) and translates them; anything
   * outside the enum is a backend bug and falls through unchanged.
   */
  getTranslatedSkillLevel(level: string): string {
    if (!level) return '';
    const normalised = level.replace(/^db\./i, '').trim().toLowerCase();
    const keyMap: { [key: string]: string } = {
      beginner: 'profile.skills.beginner',
      intermediate: 'profile.skills.intermediate',
      advanced: 'profile.skills.advanced',
      expert: 'profile.skills.expert',
    };
    const transKey = keyMap[normalised];
    return transKey ? this.translocoService.translate(transKey) : level;
  }

  /**
   * Translate language level for display
   */
  getTranslatedLanguageLevel(level: string): string {
    if (!level) return '';
    // Map language levels to translation keys
    const levelMap: { [key: string]: string } = {
      a1: 'profile.languages.a1',
      a2: 'profile.languages.a2',
      b1: 'profile.languages.b1',
      b2: 'profile.languages.b2',
      c1: 'profile.languages.c1',
      c2: 'profile.languages.c2',
    };
    return levelMap[level.toLowerCase()] ? this.translocoService.translate(levelMap[level.toLowerCase()]) : level;
  }

  /**
   * Build the proficiency-meter descriptor for a CEFR language level so the
   * row can render a coloured segmented bar (a1 = 1/6 .. c2 = 6/6). Returns
   * null for unknown levels, which hides the meter.
   */
  getLanguageLevelMeter(level: string): { filled: number; total: number; variant: string } | null {
    if (!level) return null;
    const order = ['a1', 'a2', 'b1', 'b2', 'c1', 'c2'];
    const variant = level.toLowerCase();
    const index = order.indexOf(variant);
    if (index === -1) return null;
    return { filled: index + 1, total: order.length, variant };
  }

  /**
   * Formats a date string for display
   * Converts dates from various formats to the app's localized display format.
   */
  // Using the centralized date utility function
  // This method is kept as a wrapper to maintain backward compatibility
  formatDateDisplay(dateString: string | undefined): string {
    return formatDateDisplay(dateString, this.translocoService.getActiveLang());
  }

  formatSchoolName(name: string, city: string, country: string): string {
    const locationParts = [];
    if (city) {
      locationParts.push(city);
    }
    if (country) {
      locationParts.push(country);
    }
    return name + (locationParts.length > 0 ? ' | ' + locationParts.join(', ') : '');
  }

  ngOnInit(): void {
    // Enable auto-save after a short delay to prevent immediate auto-save on page load
    setTimeout(() => {
      this.allowAutoSave = true;
    }, 2000); // 2 second delay
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Auto-save logic when data changes
    if (changes['languages'] || changes['skills'] || changes['highSchools'] || changes['universities'] || changes['workExperiences'] || changes['projects']) {
      // Skip autosave if we're in the middle of an operation
      if (this.skipNextAutoSave) {
        this.skipNextAutoSave = false;
        return;
      }

      // Only check for auto-save if enough time has passed since component initialization
      if (this.allowAutoSave) {
        this.checkAndAutoSaveItems();
      }
    }
  }

  // Check all items for auto-save opportunities
  private checkAndAutoSaveItems(): void {
    // Only batch save AI-parsed items that don't have IDs yet
    // This eliminates the individual autosave requests that were causing multiple API calls
    this.batchSaveValidAiParsedItems('languages', this.languages);
    this.batchSaveValidAiParsedItems('skills', this.skills);
    this.batchSaveValidAiParsedItems('highSchool', this.highSchools);
    this.batchSaveValidAiParsedItems('university', this.universities);
    this.batchSaveValidAiParsedItems('workExperience', this.workExperiences);
    this.batchSaveValidAiParsedItems('projects', this.projects);
  }

  // Batch save valid AI-parsed items to reduce API calls
  private batchSaveValidAiParsedItems(itemType: string, items: any[]): void {
    const validAiParsedItems = items.filter((item, index) => {
      let isValid = false;

      switch (itemType) {
        case 'languages':
          isValid = this.validationService.isLanguageValid(item);
          break;
        case 'skills':
          isValid = this.validationService.isSkillValid(item);
          break;
        case 'highSchool':
          isValid = this.validationService.isHighSchoolValid(item);
          break;
        case 'university':
          isValid = this.validationService.isUniversityValid(item);
          break;
        case 'workExperience':
          // Use centralized validation service
          isValid = this.validationService.isWorkExperienceValid(item);
          break;
        case 'projects':
          isValid = this.validationService.isProjectValid(item);
          break;
      }

      // Check for validation issues that would prevent auto-save
      if (!isValid && item.isAiParsed && !item.id) {
        let validationMessage = '';
        switch (itemType) {
          case 'languages':
            validationMessage = this.getLanguageValidationMessage(item);
            break;
          case 'skills':
            validationMessage = this.getSkillValidationMessage(item);
            break;
          case 'highSchool':
            validationMessage = this.getHighSchoolValidationMessage(item);
            break;
          case 'university':
            validationMessage = this.getUniversityValidationMessage(item);
            break;
          case 'workExperience':
            validationMessage = this.getWorkExperienceValidationMessage(item);
            break;
          case 'projects':
            validationMessage = this.getProjectValidationMessage(item);
            break;
        }
      }

      return isValid && item.isAiParsed && !item.id;
    });

    if (validAiParsedItems.length > 0) {
      // Emit a batch save action for all valid AI-parsed items
      this.profileItemAction.emit({
        itemType: itemType as any,
        action: 'batchSave',
        items: validAiParsedItems,
        isAutoSave: true,
      });
    }
  }

  // Validation methods for different data types - using centralized ValidationService
  validateLanguage(language: LanguageExtended): { isValid: boolean; message: string } {
    return {
      isValid: this.validationService.isLanguageValid(language),
      message: this.validationService.getLanguageValidationMessage(language),
    };
  }

  // Template helper methods for validation - now using centralized ValidationService
  isLanguageValid(language: LanguageExtended): boolean {
    return this.validationService.isLanguageValid(language);
  }

  getLanguageValidationMessage(language: LanguageExtended): string {
    return this.validationService.getLanguageValidationMessage(language);
  }

  isSkillValid(skill: SkillExtended): boolean {
    return this.validationService.isSkillValid(skill);
  }

  getSkillValidationMessage(skill: SkillExtended): string {
    return this.validationService.getSkillValidationMessage(skill);
  }

  isHighSchoolValid(highSchool: HighSchoolEducationExtended): boolean {
    return this.validationService.isHighSchoolValid(highSchool);
  }

  getHighSchoolValidationMessage(highSchool: HighSchoolEducationExtended): string {
    return this.validationService.getHighSchoolValidationMessage(highSchool);
  }

  isUniversityValid(university: UniversityEducationExtended): boolean {
    return this.validationService.isUniversityValid(university);
  }

  getUniversityValidationMessage(university: UniversityEducationExtended): string {
    return this.validationService.getUniversityValidationMessage(university);
  }

  isWorkExperienceValid(workExp: WorkExperienceExtended): boolean {
    return this.validationService.isWorkExperienceValid(workExp);
  }

  getWorkExperienceValidationMessage(workExp: WorkExperienceExtended): string {
    return this.validationService.getWorkExperienceValidationMessage(workExp);
  }

  isProjectValid(project: ProjectExtended): boolean {
    return this.validationService.isProjectValid(project);
  }

  getProjectValidationMessage(project: ProjectExtended): string {
    return this.validationService.getProjectValidationMessage(project);
  }

  validateSkill(skill: SkillExtended): { isValid: boolean; message: string } {
    return {
      isValid: this.validationService.isSkillValid(skill),
      message: this.validationService.getSkillValidationMessage(skill),
    };
  }

  validateHighSchool(highSchool: HighSchoolEducationExtended): { isValid: boolean; message: string } {
    return {
      isValid: this.validationService.isHighSchoolValid(highSchool),
      message: this.validationService.getHighSchoolValidationMessage(highSchool),
    };
  }

  validateUniversity(university: UniversityEducationExtended): { isValid: boolean; message: string } {
    return {
      isValid: this.validationService.isUniversityValid(university),
      message: this.validationService.getUniversityValidationMessage(university),
    };
  }

  validateWorkExperience(workExp: WorkExperienceExtended): { isValid: boolean; message: string } {
    return {
      isValid: this.validationService.isWorkExperienceValid(workExp),
      message: this.validationService.getWorkExperienceValidationMessage(workExp),
    };
  }

  validateProject(project: ProjectExtended): { isValid: boolean; message: string } {
    return {
      isValid: this.validationService.isProjectValid(project),
      message: this.validationService.getProjectValidationMessage(project),
    };
  }

  // Auto-save methods - only save if data is valid
  autoSaveIfValid(item: any, itemType: string, index: number): void {
    let validation: { isValid: boolean; message: string };

    switch (itemType) {
      case 'languages':
        validation = this.validateLanguage(item as LanguageExtended);
        break;
      case 'skills':
        validation = this.validateSkill(item as SkillExtended);
        break;
      case 'highSchool':
        validation = this.validateHighSchool(item as HighSchoolEducationExtended);
        break;
      case 'university':
        validation = this.validateUniversity(item as UniversityEducationExtended);
        break;
      case 'workExperience':
        validation = this.validateWorkExperience(item as WorkExperienceExtended);
        break;
      case 'projects':
        validation = this.validateProject(item as ProjectExtended);
        break;
      default:
        validation = { isValid: false, message: 'Unknown item type' };
    }

    // Auto-save if the item is valid and either:
    // 1. Has an ID (was previously saved)
    // 2. Is AI-parsed and doesn't have an ID yet (new AI-parsed data)
    if (validation.isValid && (item.id || (item.isAiParsed && !item.id))) {
      this.profileItemAction.emit({
        itemType: itemType as any,
        action: 'save',
        item,
        index,
        isAutoSave: true, // Mark as auto-save to suppress notifications
      });
    }
  }

  /**
   * Private method to emit unified profile item action events
   */
  private emitProfileItemAction(
    itemType: 'languages' | 'skills' | 'highSchool' | 'university' | 'workExperience' | 'projects',
    action: 'add' | 'edit' | 'delete' | 'save',
    item?: LanguageExtended | SkillExtended | HighSchoolEducationExtended | UniversityEducationExtended | WorkExperienceExtended | ProjectExtended,
    index?: number,
    isAutoSave?: boolean
  ): void {
    // Emit the unified event
    this.profileItemAction.emit({ itemType, action, item, index, isAutoSave });
  }

  // Language handlers
  onSaveLanguage(languageData: Partial<LanguageExtended>): void {
    this.skipNextAutoSave = true; // Prevent autosave after this operation
    const action = this.editingIndex !== null ? 'edit' : 'add';
    this.emitProfileItemAction('languages', action, languageData as LanguageExtended, this.editingIndex ?? undefined);
    this.resetLanguageForm();
  }

  onEditLanguage(index: number): void {
    this.editingLanguage = this.languages[index];
    this.editingIndex = index;
    this.showLanguageForm = true;
  }

  onDeleteLanguage(index: number): void {
    this.skipNextAutoSave = true; // Prevent autosave after this operation
    this.emitProfileItemAction('languages', 'delete', undefined, index);
  }

  onCancelLanguageForm(): void {
    this.resetLanguageForm();
  }

  private resetLanguageForm(): void {
    this.showLanguageForm = false;
    this.editingLanguage = null;
    this.editingIndex = null;
  }

  // Skill handlers
  onSaveSkill(skillData: Partial<SkillExtended>): void {
    this.skipNextAutoSave = true; // Prevent autosave after this operation
    const action = this.editingIndex !== null ? 'edit' : 'add';
    this.emitProfileItemAction('skills', action, skillData as SkillExtended, this.editingIndex ?? undefined);
    this.resetSkillForm();
  }

  onEditSkill(index: number): void {
    this.editingSkill = this.skills[index];
    this.editingIndex = index;
    this.showSkillForm = true;
  }

  onDeleteSkill(index: number): void {
    this.skipNextAutoSave = true; // Prevent autosave after this operation
    this.emitProfileItemAction('skills', 'delete', undefined, index);
  }

  onCancelSkillForm(): void {
    this.resetSkillForm();
  }

  private resetSkillForm(): void {
    this.showSkillForm = false;
    this.editingSkill = null;
    this.editingIndex = null;
  }

  // High School Education handlers
  onSaveHighSchool(highSchoolData: Partial<HighSchoolEducationExtended>): void {
    this.skipNextAutoSave = true; // Prevent autosave after this operation
    const action = this.editingIndex !== null ? 'edit' : 'add';
    this.emitProfileItemAction('highSchool', action, highSchoolData as HighSchoolEducationExtended, this.editingIndex ?? undefined);
    this.resetHighSchoolForm();
  }

  onEditHighSchool(index: number): void {
    this.editingHighSchool = this.highSchools[index];
    this.editingIndex = index;
    this.showHighSchoolForm = true;
  }

  onDeleteHighSchool(index: number): void {
    this.skipNextAutoSave = true; // Prevent autosave after this operation
    this.emitProfileItemAction('highSchool', 'delete', undefined, index);
  }

  onCancelHighSchoolForm(): void {
    this.resetHighSchoolForm();
  }

  private resetHighSchoolForm(): void {
    this.showHighSchoolForm = false;
    this.editingHighSchool = null;
    this.editingIndex = null;
  }

  // University Education handlers
  onSaveUniversity(universityData: Partial<UniversityEducationExtended>): void {
    this.skipNextAutoSave = true; // Prevent autosave after this operation
    const action = this.editingIndex !== null ? 'edit' : 'add';
    this.emitProfileItemAction('university', action, universityData as UniversityEducationExtended, this.editingIndex ?? undefined);
    this.resetUniversityForm();
  }

  onEditUniversity(index: number): void {
    this.editingUniversity = this.universities[index];
    this.editingIndex = index;
    this.showUniversityForm = true;
  }

  onDeleteUniversity(index: number): void {
    this.skipNextAutoSave = true; // Prevent autosave after this operation
    this.emitProfileItemAction('university', 'delete', undefined, index);
  }

  onCancelUniversityForm(): void {
    this.resetUniversityForm();
  }

  private resetUniversityForm(): void {
    this.showUniversityForm = false;
    this.editingUniversity = null;
    this.editingIndex = null;
  }

  // Work Experience handlers
  onSaveWorkExperience(workExperienceData: Partial<WorkExperienceExtended>): void {
    this.skipNextAutoSave = true; // Prevent autosave after this operation
    const action = this.editingIndex !== null ? 'edit' : 'add';
    this.emitProfileItemAction('workExperience', action, workExperienceData as WorkExperienceExtended, this.editingIndex ?? undefined);
    this.resetWorkExperienceForm();
  }

  onEditWorkExperience(index: number): void {
    this.editingWorkExperience = this.workExperiences[index];
    this.editingIndex = index;
    this.showWorkExperienceForm = true;
  }

  onDeleteWorkExperience(index: number): void {
    this.skipNextAutoSave = true; // Prevent autosave after this operation
    this.emitProfileItemAction('workExperience', 'delete', undefined, index);
  }

  onCancelWorkExperienceForm(): void {
    this.resetWorkExperienceForm();
  }

  private resetWorkExperienceForm(): void {
    this.showWorkExperienceForm = false;
    this.editingWorkExperience = null;
    this.editingIndex = null;
  }

  // Project handlers
  onSaveProject(projectData: Partial<ProjectExtended>): void {
    this.skipNextAutoSave = true; // Prevent autosave after this operation
    const action = this.editingIndex !== null ? 'edit' : 'add';
    this.emitProfileItemAction('projects', action, projectData as ProjectExtended, this.editingIndex ?? undefined);
    this.resetProjectForm();
  }

  onEditProject(index: number): void {
    this.editingProject = this.projects[index];
    this.editingIndex = index;
    this.showProjectForm = true;
  }

  onDeleteProject(index: number): void {
    this.skipNextAutoSave = true; // Prevent autosave after this operation
    this.emitProfileItemAction('projects', 'delete', undefined, index);
  }

  onCancelProjectForm(): void {
    this.resetProjectForm();
  }

  private resetProjectForm(): void {
    this.showProjectForm = false;
    this.editingProject = null;
    this.editingIndex = null;
  }

  // Individual save row methods for AI-parsed data
  onSaveLanguageRow(index: number): void {
    const language = this.languages[index];
    if (language && language.isAiParsed && !language.id) {
      // Mark as no longer AI parsed since user is manually saving it
      language.isAiParsed = false;
      // Emit a special save action for individual items
      this.emitProfileItemAction('languages', 'save' as any, language, index, false);
    }
  }

  onSaveSkillRow(index: number): void {
    const skill = this.skills[index];
    if (skill && skill.isAiParsed && !skill.id) {
      // Mark as no longer AI parsed since user is manually saving it
      skill.isAiParsed = false;
      // Emit a special save action for individual items
      this.emitProfileItemAction('skills', 'save' as any, skill, index, false);
    }
  }

  onSaveHighSchoolRow(index: number): void {
    const highSchool = this.highSchools[index];
    if (highSchool && highSchool.isAiParsed && !highSchool.id) {
      if (!this.validationService.isHighSchoolValid(highSchool)) {
        return;
      }

      // Mark as no longer AI parsed since user is manually saving it
      highSchool.isAiParsed = false;
      // Emit a special save action for individual items
      this.emitProfileItemAction('highSchool', 'save' as any, highSchool, index, false);
    }
  }

  onSaveUniversityRow(index: number): void {
    const university = this.universities[index];
    if (university && university.isAiParsed && !university.id) {
      if (!this.validationService.isUniversityValid(university)) {
        return;
      }

      // Mark as no longer AI parsed since user is manually saving it
      university.isAiParsed = false;
      // Emit a special save action for individual items
      this.emitProfileItemAction('university', 'save' as any, university, index, false);
    }
  }
  onSaveWorkExperienceRow(index: number): void {
    const workExperience = this.workExperiences[index];
    if (workExperience && workExperience.isAiParsed && !workExperience.id) {
      // First, check validation using ValidationService for consistency
      if (!this.validationService.isWorkExperienceValid(workExperience)) {
        console.error('Work experience validation failed');
        return;
      }

      // Perform full validation
      const validation = this.validateWorkExperience(workExperience);
      if (!validation.isValid) {
        // If invalid, don't attempt to save
        // The row will continue to show validation errors in the UI
        return;
      }

      // Mark as no longer AI parsed since user is manually saving it
      workExperience.isAiParsed = false;
      // Emit a special save action for individual items
      this.emitProfileItemAction('workExperience', 'save' as any, workExperience, index, false);
    }
  }

  onSaveProjectRow(index: number): void {
    const project = this.projects[index];
    if (project && project.isAiParsed && !project.id) {
      // Mark as no longer AI parsed since user is manually saving it
      project.isAiParsed = false;
      // Emit a special save action for individual items
      this.emitProfileItemAction('projects', 'save' as any, project, index, false);
    }
  }
}
